import { SPELL_REGISTRY } from '../data/spells.config';
import { BUFF_REGISTRY } from '../data/buffs.config';
import {
  TRINKET_MAJOR_REGISTRY,
  TRINKET_MINOR_REGISTRY,
  buildSpellFromTemplate,
} from '../data/trinkets.config';
import { EMOTION_NAMES, EMOTION_COLORS } from '../data/emotions.config';
import { EffectShape, ELEMENTS, type Element } from '../types/enums';
import type { SpellDefinition, SpellEffect } from '../types/spell.types';
import type { BuffDefinition } from '../types/buff.types';
import type { EffectStep, EffectCondition } from '../types/effect.types';
import type {
  TrinketMajorDefinition,
  TrinketMinorDefinition,
} from '../types/trinket.types';
import {
  describeBuff as describeBuffLines,
  describeTrinketCurrent,
  type DescLine,
  type DescTone,
} from './TrinketDescriptionService';

export type { DescLine } from './TrinketDescriptionService';

function formatSeconds(ms: number): string {
  return `${Number((ms / 1000).toFixed(1))} s`;
}

export type CardRef = string; // 'spell:<id>' | 'buff:<id>' | 'major:<id>' | 'minor:<id>'

export interface SpellStat {
  label: string;
  value: string;
  color?: string;
  tone?: DescTone;
}

export interface Card {
  kind: 'spell' | 'trinket-major' | 'trinket-minor' | 'buff';
  id: string;
  icon?: string;
  name: string;
  stats?: SpellStat[];
  lines: DescLine[];
  related: CardRef[];
}

function effectElement(effects: SpellEffect[]): Element | undefined {
  for (const e of effects) {
    const el = (e.params as Record<string, unknown>).element;
    if (
      typeof el === 'string' &&
      (ELEMENTS as readonly string[]).includes(el)
    ) {
      return el as Element;
    }
  }
  return undefined;
}

function buffRefsFromEffects(effects: EffectStep[]): CardRef[] {
  const refs: CardRef[] = [];
  for (const e of effects) {
    const id = (e.params as Record<string, unknown>).buffId;
    if (typeof id === 'string' && BUFF_REGISTRY[id]) {
      refs.push(`buff:${id}`);
    }
  }
  return refs;
}

export function describeBuff(buff: BuffDefinition): Card {
  const stats: SpellStat[] = [];
  if (buff.durationMs) {
    stats.push({
      label: 'Durée',
      value: formatSeconds(buff.durationMs),
    });
  }
  if (buff.maxStacks) {
    stats.push({ label: 'Stacks max', value: String(buff.maxStacks) });
  }
  if (buff.dot) {
    stats.push({
      label: 'DOT',
      value: `${buff.dot.amount}/${buff.dot.intervalMs === 1000 ? 's' : formatSeconds(buff.dot.intervalMs)} (${buff.dot.type === 'physical' ? 'physique' : 'magique'})`,
      tone: 'loss',
    });
  }
  const conditionRefs: CardRef[] = [];
  for (const c of buff.damageMultiplierConditions ?? []) {
    for (const cond of c.conditions ?? []) {
      const id = (cond.params as Record<string, unknown> | undefined)?.buffId;
      if (typeof id === 'string' && BUFF_REGISTRY[id]) {
        conditionRefs.push(`buff:${id}`);
      }
    }
  }
  const related = [
    ...buffRefsFromEffects(buff.onMaxStacks ?? []),
    ...buffRefsFromEffects(buff.hooks ?? []),
    ...conditionRefs,
  ];
  // On the buff's own card the dot is already a stat — the shared line
  // describer's "Inflige <own name>" would be self-referential noise.
  const lines = describeBuffLines(buff).filter(
    (l) => l.label !== `Inflige ${buff.name}`,
  );
  return {
    kind: 'buff',
    id: buff.id,
    icon: buff.icon,
    name: buff.name,
    stats: stats.length ? stats : undefined,
    lines,
    related,
  };
}

const SHAPE_LABELS: Partial<Record<EffectShape, string>> = {
  [EffectShape.CIRCLE]: 'Cercle',
  [EffectShape.CONE]: 'Cône',
  [EffectShape.LINE]: 'Ligne',
  [EffectShape.SWIPE]: 'Arc',
};

function categoryStat(def: SpellDefinition): string {
  const shape = def.shapes?.[0];
  const range = def.range ?? 1;
  const hasDash = def.effects.some((e) => e.handler === 'dashToTarget');
  const hitscan = def.effects.find((e) => e.handler === 'hitscan');
  let base: string;
  if (def.projectile) {
    base = `Projectile (portée ${def.projectile.range ?? range})`;
  } else if (hitscan) {
    const hr = (hitscan.params as Record<string, unknown>).range;
    base = `Rayon (portée ${typeof hr === 'number' ? hr : range})`;
  } else if (def.parameter.type === 'self') {
    base = def.shapes?.length ? 'Aura (sur soi)' : 'Sur soi';
  } else if (shape && SHAPE_LABELS[shape.type]) {
    const r = shape.range !== undefined ? ` rayon ${shape.range}` : '';
    base = `Zone — ${SHAPE_LABELS[shape.type]}${r} (portée ${range})`;
  } else if (range <= 1) {
    base = 'Mêlée';
  } else {
    base = `À distance (portée ${range})`;
  }
  return hasDash ? `${base} + déplacement` : base;
}

function damageStat(def: SpellDefinition): string | undefined {
  for (const e of def.effects) {
    if (e.handler === 'dealDamage' || e.handler === 'dealDamageInCells') {
      const p = e.params as Record<string, unknown>;
      const amount = typeof p.amount === 'number' ? p.amount : 0;
      const type = p.type === 'physical' ? 'physique' : 'magique';
      const ls =
        typeof p.lifeStealPercent === 'number'
          ? ` · vol de vie ${Math.round(p.lifeStealPercent * 100)} %`
          : '';
      return `${amount} (${type})${ls}`;
    }
  }
  return undefined;
}

function spellEffectLines(def: SpellDefinition): DescLine[] {
  const lines: DescLine[] = [];
  for (const e of def.effects) {
    const p = e.params as Record<string, unknown>;
    switch (e.handler) {
      // Self-targeted buffs read "Gagne" (the player earns them); cell-targeted
      // ones read "Inflige" (enemies suffer them).
      case 'applyBuff':
      case 'addBuffStack': {
        const id = typeof p.buffId === 'string' ? p.buffId : undefined;
        const buff = id ? BUFF_REGISTRY[id] : undefined;
        if (buff) {
          lines.push({ label: `Gagne ${buff.name}`, tone: 'gain' });
        }
        break;
      }
      case 'applyBuffInCells':
      case 'applyBuffWithChance':
      case 'addBuffStackInCells': {
        const id = typeof p.buffId === 'string' ? p.buffId : undefined;
        const buff = id ? BUFF_REGISTRY[id] : undefined;
        if (buff) {
          const chance = typeof p.chance === 'number' ? ` (${p.chance} %)` : '';
          lines.push({ label: `Inflige ${buff.name}${chance}`, tone: 'gain' });
        }
        break;
      }
      case 'knockback':
      case 'knockbackAll':
        lines.push({
          label: `Repousse de ${typeof p.distance === 'number' ? p.distance : 1}`,
          tone: 'gain',
        });
        break;
      case 'healShield':
        lines.push({
          label: 'Bouclier',
          delta: {
            value: typeof p.amount === 'number' ? p.amount : 0,
            percent: false,
          },
          tone: 'gain',
        });
        break;
      case 'dealSelfDamage':
        lines.push({
          label: 'Dégâts à soi',
          delta: {
            value: typeof p.amount === 'number' ? p.amount : 0,
            percent: false,
          },
          tone: 'loss',
        });
        break;
      case 'dashToTarget':
        lines.push({ label: 'Se rue sur la cible', tone: 'neutral' });
        break;
      case 'spawnMonsters':
        lines.push({ label: 'Invoque des monstres', tone: 'neutral' });
        break;
      default:
        break;
    }
  }
  return lines;
}

export function describeSpell(def: SpellDefinition): Card {
  const element = effectElement(def.effects);
  const stats: SpellStat[] = [
    element
      ? {
          label: 'Élément',
          value: EMOTION_NAMES[element],
          color: EMOTION_COLORS[element],
        }
      : { label: 'Élément', value: 'Neutre' },
    { label: 'Catégorie', value: categoryStat(def) },
  ];
  const dmg = damageStat(def);
  stats.push({
    label: 'Dégâts',
    value: dmg ?? '—',
    tone: dmg ? 'loss' : 'neutral',
  });
  stats.push({
    label: 'Recharge',
    value: def.cooldownMs ? formatSeconds(def.cooldownMs) : '—',
  });
  return {
    kind: 'spell',
    id: def.id,
    icon: def.icon,
    name: def.name,
    stats,
    lines: spellEffectLines(def),
    related: buffRefsFromEffects(def.effects),
  };
}

function collectStepBuffRefs(step: EffectStep, refs: Set<CardRef>): void {
  const p = step.params as Record<string, unknown>;
  for (const key of ['buffId', 'conditionBuffId']) {
    const id = p[key];
    if (typeof id === 'string') {
      if (BUFF_REGISTRY[id]) {
        refs.add(`buff:${id}`);
      }
    }
  }
  if (step.handler === 'grantPassive') {
    const pd = p as {
      hooks?: EffectStep[];
      onApply?: EffectStep[];
      damageMultiplierConditions?: { conditions?: EffectCondition[] }[];
      damageTakenMultiplierConditions?: { conditions?: EffectCondition[] }[];
    };
    for (const sub of [...(pd.hooks ?? []), ...(pd.onApply ?? [])]) {
      collectStepBuffRefs(sub, refs);
    }
    const condGroups = [
      ...(pd.damageMultiplierConditions ?? []),
      ...(pd.damageTakenMultiplierConditions ?? []),
    ];
    for (const group of condGroups) {
      for (const cond of group.conditions ?? []) {
        const id = (cond.params as Record<string, unknown> | undefined)?.buffId;
        if (typeof id === 'string' && BUFF_REGISTRY[id]) {
          refs.add(`buff:${id}`);
        }
      }
    }
  }
  const inner = p.step;
  if (inner && typeof inner === 'object' && 'handler' in inner) {
    collectStepBuffRefs(inner as EffectStep, refs);
  }
}

// Pure-stat per-level bonuses never get a related card (they're plain lines on
// the trinket card); any behavioral buff the trinket's effects mention does — e.g. a
// minor scaling on Tempo stacks links the Tempo card.
function trinketRelatedRefs(
  def: TrinketMajorDefinition | TrinketMinorDefinition,
  level: number,
): Set<CardRef> {
  const refs = new Set<CardRef>();
  for (let lvl = 1; lvl <= level; lvl++) {
    const effects = def.effectsByLevel?.[lvl];
    const steps = [
      ...(effects?.global ?? []),
      ...(effects?.onAttachedSpell ?? []),
    ];
    for (const step of steps) {
      collectStepBuffRefs(step, refs);
    }
  }
  return refs;
}

export function describeTrinketMajorCard(
  id: string,
  level: number,
): Card | null {
  const def = TRINKET_MAJOR_REGISTRY[id];
  if (!def) {
    return null;
  }
  const basic = buildSpellFromTemplate(def.basicAttack, `trinket-attack:${id}`);
  const basicCard = describeSpell(basic);
  const lines: DescLine[] = [
    { label: '— Attaque de base —', tone: 'neutral' },
    ...basicCard.lines,
    { label: '— Effets de trinket —', tone: 'neutral' },
    ...describeTrinketCurrent(def, level),
  ];
  return {
    kind: 'trinket-major',
    id,
    icon: def.icon,
    name: def.name,
    stats: basicCard.stats,
    lines,
    related: [
      ...new Set([...basicCard.related, ...trinketRelatedRefs(def, level)]),
    ],
  };
}

export function trinketCard(
  modelId: string,
  level: number,
  isMajor: boolean,
): Card | null {
  if (isMajor) {
    return describeTrinketMajorCard(modelId, level);
  }
  const def = TRINKET_MINOR_REGISTRY[modelId];
  return def
    ? {
        kind: 'trinket-minor',
        id: modelId,
        icon: def.icon,
        name: def.name,
        lines: describeTrinketCurrent(def, level),
        related: [...trinketRelatedRefs(def, level)],
      }
    : null;
}

export function cardToData(card: Card): {
  stats?: SpellStat[];
  lines: DescLine[];
  rootCard: Card;
} {
  return { stats: card.stats, lines: card.lines, rootCard: card };
}

export function describeItemCard(
  itemId: string,
): { stats?: SpellStat[]; lines: DescLine[]; rootCard: Card } | null {
  if (itemId.startsWith('grimoire-')) {
    const def = SPELL_REGISTRY[itemId.slice('grimoire-'.length)];
    return def ? cardToData(describeSpell(def)) : null;
  }
  return null;
}

function cardForRef(ref: CardRef): Card | null {
  const [kind, id] = ref.split(/:(.+)/);
  switch (kind) {
    case 'spell': {
      const def = SPELL_REGISTRY[id];
      return def ? describeSpell(def) : null;
    }
    case 'buff': {
      const buff = BUFF_REGISTRY[id];
      return buff ? describeBuff(buff) : null;
    }
    case 'minor': {
      return trinketCard(id, 1, false);
    }
    case 'major': {
      return describeTrinketMajorCard(id, 1);
    }
    default:
      return null;
  }
}

const KIND_TO_REF: Record<Card['kind'], string> = {
  spell: 'spell',
  buff: 'buff',
  'trinket-major': 'major',
  'trinket-minor': 'minor',
};

export function resolveCardClosure(root: Card): Card[] {
  const out: Card[] = [root];
  const seen = new Set<string>([`${KIND_TO_REF[root.kind]}:${root.id}`]);
  const queue: CardRef[] = [...root.related];
  while (queue.length) {
    const ref = queue.shift()!;
    if (seen.has(ref)) {
      continue;
    }
    seen.add(ref);
    const card = cardForRef(ref);
    if (card) {
      out.push(card);
      queue.push(...card.related.filter((r) => !seen.has(r)));
    }
  }
  return out;
}
